"use client";

import { c as _c } from "react/compiler-runtime";
import VideoPlayer from "@/components/fancy/video-player";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogHeader, DialogTag, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { TCourse } from "@/types/types";
import { formatDuration } from "@/utils/formatters";
import { cn } from "@/utils/helpers";
import { getVideoUrl } from "@/utils/media-utils";
import { Play, PlayCircle, X } from "lucide-react";
import Image from "next/image";
import { useState } from "react";
type CoursePreviewDialogProps = {
  course: TCourse;
  className?: string;
};
export default function CoursePreviewDialog(t0) {
  const $ = _c(36);
  const {
    course,
    className
  } = t0;
  const [activeVideo, setActiveVideo] = useState(course.trailerVideo);
  let t1;
  if ($[0] !== course.sections) {
    t1 = course.sections.flatMap(_temp).map(_temp2).filter(_temp3);
    $[0] = course.sections;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const freeChapters = t1;
  let t2;
  if ($[2] !== className) {
    t2 = cn("group relative flex aspect-video h-full w-full", className);
    $[2] = className;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== course.thumbnailImage?.url || $[5] !== course.title) {
    t3 = course.thumbnailImage?.url ? <Image src={course.thumbnailImage?.url} alt={`Thumbnail image of course: ${course.title}`} width={400} height={200} priority={true} className="h-full w-full object-cover" /> : null;
    $[4] = course.thumbnailImage?.url;
    $[5] = course.title;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="absolute h-full w-full bg-gradient-to-b from-transparent to-dark/70" />;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="absolute left-1/2 top-1/2 flex size-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-light text-primaryX transition group-hover:bg-primaryX group-hover:text-light"><Play size={24} /></div>;
    t6 = <p className="absolute bottom-4 left-1/2 -translate-x-1/2 text-base font-bold text-light">Preview the course</p>;
    $[8] = t5;
    $[9] = t6;
  } else {
    t5 = $[8];
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t2 || $[11] !== t3) {
    t7 = <DialogTrigger className={t2}>{t3}{t4}{t5}{t6}</DialogTrigger>;
    $[10] = t2;
    $[11] = t3;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <DialogTag>Course Preview</DialogTag>;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== course.title) {
    t9 = <DialogTitle>{course.title}</DialogTitle>;
    $[14] = course.title;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <DialogDescription />;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== t9) {
    t11 = <div className="overflow-hidden">{t8}{t9}{t10}</div>;
    $[17] = t9;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <DialogClose className="ml-auto"><X size={18} /></DialogClose>;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== t11) {
    t13 = <DialogHeader className="h-12 flex-row items-start">{t11}{t12}</DialogHeader>;
    $[20] = t11;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== activeVideo) {
    t14 = activeVideo ? <VideoPlayer videoUrl={getVideoUrl(activeVideo)} autoPlay={true} /> : null;
    $[22] = activeVideo;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== t14) {
    t15 = <div className="flex aspect-video w-full bg-muted">{t14}</div>;
    $[24] = t14;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== course.sections || $[27] !== freeChapters.length) {
    t16 = freeChapters.length > 0 ? <><DialogTag className="mt-4">Free Videos</DialogTag><ul>{course.sections.map(section_0 => section_0.chapters.map(chapter_1 => chapter_1.isFree ? <li key={chapter_1.id} className="flex w-full cursor-pointer items-center gap-3 rounded-lg px-2 py-4 hover:bg-muted" onClick={() => setActiveVideo(chapter_1.chapterVideo)}><div className="aspect-video w-12 overflow-hidden rounded-md bg-muted">{chapter_1.chapterVideo?.thumbnailUrl ? <Image src={chapter_1.chapterVideo.thumbnailUrl} alt={`Thumbnail image of chapter: ${chapter_1.title}`} width={48} height={48} className="object-cover" /> : null}</div><PlayCircle size={18} /><div className="flex w-full items-center justify-between"><p className="text-sm font-semibold">{chapter_1.title}</p><span className="text-sm">{formatDuration(chapter_1.chapterVideo?.duration || 0, "basic")}</span></div></li> : null))}</ul></> : null;
    $[26] = course.sections;
    $[27] = freeChapters.length;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== t13 || $[30] !== t15 || $[31] !== t16) {
    t17 = <DialogContent className="hide-scrollbar max-h-[calc(100vh-50px)] overflow-auto">{t13}{t15}{t16}</DialogContent>;
    $[29] = t13;
    $[30] = t15;
    $[31] = t16;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== t17 || $[34] !== t7) {
    t18 = <Dialog>{t7}{t17}</Dialog>;
    $[33] = t17;
    $[34] = t7;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  return t18;
}
function _temp3(chapter_0) {
  return chapter_0.isFree;
}
function _temp2(chapter) {
  return chapter;
}
function _temp(section) {
  return section.chapters;
}